import {
  Component,
  OnInit,
  OnChanges,
  Input,
  HostListener,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  SimpleChanges
} from '@angular/core';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { ApiResponse, SelectedGeolocation } from '../../models/v2/search';
import { autoSuggestionLocation, autoLocationList } from 'src/app/shared/models/v1/location';
import { debounceTime, map } from 'rxjs/operators';
import { GeolocationService } from '../../services/geolocation/geolocation.service';
import { LocationByIpResult, LocationConverter, SelectedLocation } from 'src/app/core/models/location';
import { InstanceService } from '@commonspirit/ngx-instance';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { EmbedSource, getEmbedSource } from 'src/app/core/util/root-element';
import { ConstantPool } from '@angular/compiler';

@Component({
  selector: 'fal-geolocation-search-box',
  templateUrl: './geolocation-search-box.component.html',
  styleUrls: ['./geolocation-search-box.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(500, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))])
    ])
  ]
})
export class GeolocationSearchBoxComponent implements OnInit, OnChanges {
  @Input() selectedGeolocation: SelectedGeolocation | null;
  @Input() invalidGeolocation: boolean;
  @Input() latitude: number;
  @Input() longitude: number;
  @Input() showSearchBtn?: boolean;
  @Output() geoSearchTermChanged = new EventEmitter<string>();
  @Output() setGeolocation = new EventEmitter<SelectedGeolocation>();
  @Output() locationEnter = new EventEmitter<string>();
  @Input() hideLocationSuggestions: boolean;
  @Output() tabPressedOnLocationSearch = new EventEmitter<any>();

  geoLocationStatus: boolean;
  autocompleteActive: boolean;
  geoAutoLocations: autoLocationList[] = [];
  geolocations: SelectedGeolocation[] = [];
  geolocationName = '';
  highlightedGeolocation = -1;
  results$: Subscription;
  subject = new ReplaySubject<string>(1);
  searchLocation: string;
  private autoLocationSub: Subscription;
  private currentLocationSub: Subscription;
  autosuggestionsLocation: autoSuggestionLocation = {
    result: []
  };
  googlePlaceId: string;
  private readonly inputDebounceMilliseconds = 300;
  private inputModelChanged: Subject<string> = new Subject<string>();
  loadSpinnerSuggestions = false;
  isAEMEnvironment = false;
  inputInFocus = false;
  searchSelected = false;

  @ViewChild('geoLocationSearchContainer') geoLocationSearchContainer: ElementRef;
  @ViewChild('geolocationInput') geolocationInput: ElementRef;
  loadSuggestions: string;

  @HostListener('window:click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.geolocationInput && this.geolocationInput.nativeElement.contains(event.target)) {
      if (this.geolocationName.length > 0 && !this.selectedGeolocation) {
        this.setAutocompleteActive(true);
      }
    } else {
      this.setAutocompleteActive(false);
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(keyEvent: KeyboardEvent) {
    const keyCode = keyEvent.key.toLowerCase();
    if (keyCode === 'tab' && this.autocompleteActive) {
      this.tabPressedOnLocationSearch.emit();
    }
  }

  constructor(private geolocationService: GeolocationService, private instanceService: InstanceService) {
    // intentionally empty
  }

  ngOnInit(): void {
    const embedSource = getEmbedSource();
    if (embedSource === EmbedSource.AEM) {
      this.isAEMEnvironment = true;
    }
    this.loadSuggestions = this.instanceService.getConfiguration(ConfigurationKey.LoadingSuggestion)?.value as string;
    window.sessionStorage.removeItem('geolocation-sessionId');

    this.inputModelChanged.pipe(debounceTime(this.inputDebounceMilliseconds)).subscribe((val) => {
      this.onChangeSearch(val);
      this.geolocationName = val;
    });
  }

  onInputChange(val: string): void {
    this.inputModelChanged.next(val);
    this.geoSearchTermChanged.emit(val);
    this.searchSelected = false;
  }

  onChangeSearch(val: string): void {
    this.searchLocation = val.trim();
    this.geoSearchTermChanged.emit(val);
    this.autosuggestionsLocation = {
      result: []
    };
    if (this.searchLocation.length >= 2) {
      this.setAutocompleteActive(false);
      this.autoLocationSub = this.geolocationService.getGeolocationSuggestions(this.searchLocation).subscribe(
        (result) => {
          this.autosuggestionsLocation = result;
          this.geolocations = result.result.map(
            (l) =>
              ({
                name: l.locationName,
                googlePlaceId: l.googlePlaceId,
                address: '',
                zip: '',
                city: '',
                state: '',
                neighborhood: ''
              } as SelectedGeolocation)
          );
          this.setAutocompleteActive(true);
          this.loadSpinnerSuggestions = false;
        },
        (error: Error) => {
          this.loadSpinnerSuggestions = false;
          console.log(`no result: ${error.message}`);
        }
      );
    }
  }

  async findCurrentLocation(): Promise<void> {
    await new Promise<void>(() => {
      this.getGeoLocation();
    });
  }

  private getGeoLocation() {
    this.geolocationService
      .getLocationByIP()
      .pipe(map((result: ApiResponse<LocationByIpResult>) => LocationConverter.convertTo(result.result.location)))
      .subscribe((location: SelectedLocation) => {
        this.latitude = location.latitude;
        this.longitude = location.longitude;
        this.setGeolocation.emit(location);
        this.geoSearchTermChanged.emit(JSON.stringify(location.name));
      });
  }

  findGeolocation(): void {
    if (this.geoLocationStatus) {
      this.currentLocationSub = this.geolocationService.getGeolocationByCoordinates(this.latitude, this.longitude).subscribe((response) => {
        this.geolocationName = response.result.location.name;
        this.geoSearchTermChanged.emit(JSON.stringify(this.geolocationName).slice(1, -1));
      });
    }
  }

  setSelectedGeolocation(geolocation: autoLocationList): void {
    this.searchSelected = true;
    const zipPattern = /\d+/g;
    const isZipExist = geolocation.locationName.match(zipPattern);
    this.geolocationName = geolocation.locationName;
    this.geoSearchTermChanged.emit(geolocation.locationName);
    this.googlePlaceId = geolocation.googlePlaceId;
    this.autoLocationSub = this.geolocationService.getGeolocationDetail(this.googlePlaceId).subscribe(
      (response) => {
        const geolocationDetailResult = response.result;
        const geolocation = this.geolocationService.mapToSelectedGeolocation(this.googlePlaceId, geolocationDetailResult, isZipExist);

        this.setGeolocation.emit(geolocation);

        this.autosuggestionsLocation.result = [];
        this.setAutocompleteActive(false);
      },
      (error: Error) => console.log(`no result: ${error.message}`)
    );
  }

  clearSession(): void {
    this.setAutocompleteActive(false);
    window.sessionStorage.removeItem('location-sessionId');
  }

  setAutocompleteActive(value: boolean): void {
    this.autocompleteActive = value;
    if (value) {
      this.geolocationInput.nativeElement.style.borderRadius = '6px 0 0 0';
    } else {
      this.geolocationInput.nativeElement.style.borderRadius = '6px 0 0 6px';
    }
  }

  handleGeolocationEnter(): void {
    if (this.autocompleteActive && this.highlightedGeolocation >= 0 && this.highlightedGeolocation <= this.geolocations.length) {
      this.setSelectedGeolocation(this.autosuggestionsLocation.result[this.highlightedGeolocation]);
    }

    this.locationEnter.emit(this.searchLocation);
  }

  setIndex(index: number): void {
    if (index <= -1 || index >= this.geolocations.length) {
      return;
    }

    this.highlightedGeolocation = index;
  }

  handleKeyPress($event: KeyboardEvent): void {
    const keyCode = $event.key.toLowerCase();
    if (keyCode === 'arrowdown') {
      $event.preventDefault();
      this.setIndex(this.highlightedGeolocation + 1);
    } else if (keyCode === 'arrowup') {
      $event.preventDefault();
      this.setIndex(this.highlightedGeolocation - 1);
    } else if (keyCode === 'enter') {
      this.setAutocompleteActive(false);
    } else if (keyCode === 'delete') {
      this.setAutocompleteActive(false);
    } else if (keyCode != 'tab') {
      if (this.geolocationName.trim().length > 1) {
        this.loadSpinnerSuggestions = true;
        this.setAutocompleteActive(true);
      } else {
        this.loadSpinnerSuggestions = false;
        this.setAutocompleteActive(false);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.hideLocationSuggestions?.currentValue) {
      this.setAutocompleteActive(false);
    }
    if (changes?.selectedGeolocation?.currentValue != changes?.selectedGeolocation?.previousValue) {
      this.geolocationName = this.selectedGeolocation?.name ?? '';
    }
  }

  onFocusGained(): void {
    this.inputInFocus = true;
  }

  onFocusLost(event: FocusEvent | any): void {
    if (
      (event.target && !this.geoLocationSearchContainer.nativeElement.contains(event.target)) ||
      (event.relatedTarget && !this.geoLocationSearchContainer.nativeElement.contains(event.relatedTarget))
    ) {
      this.inputInFocus = false;
    }
  }

  get showError(): boolean {
    return (
      !this.autocompleteActive && this.geolocationName !== '' && this.autosuggestionsLocation.result.length > 0 && !this.searchSelected
    );
  }
}
